import { ENV } from "@/config/env";
import { useMemo } from "react";
import { Helmet } from "react-helmet-async";
import { createOrganizationSchema, createWebSiteSchema } from "./schemas";
import {
    buildCanonicalUrl,
    buildDescription,
    buildTitle,
    getImageUrl,
    normalizeKeywords,
} from "./seoUtils";
import { BRAND_KEYWORDS, SITE_NAME } from "./siteConfig";

const normalizeSchema = (schema) => {
  if (!schema) return [];
  return Array.isArray(schema) ? schema.filter(Boolean) : [schema];
};

const Seo = ({
  title,
  description,
  keywords,
  canonicalPath,
  canonicalUrl,
  image,
  type = "website",
  noIndex = false,
  noFollow = false,
  locale = "en_IN",
  schema,
  robots,
}) => {
  const metaTitle = buildTitle(title);
  const metaDescription = buildDescription(
    description,
    "Authentic Rudraksha beads, malas, and spiritual guidance from Subrahmanya Rudraksha.",
  );
  const metaKeywords = normalizeKeywords(keywords || BRAND_KEYWORDS);
  const canonical = canonicalUrl || buildCanonicalUrl(canonicalPath || "/");
  const ogImage = getImageUrl(image);

  const scripts = useMemo(() => {
    const structuredData = [
      createOrganizationSchema(),
      createWebSiteSchema(),
      ...normalizeSchema(schema),
    ].filter(Boolean);

    return structuredData.map((entry) => ({
      type: "application/ld+json",
      innerHTML: JSON.stringify(entry),
    }));
  }, [schema]);

  const robotsContent =
    robots ||
    `${noIndex ? "noindex" : "index"},${noFollow ? "nofollow" : "follow"},max-image-preview:large,max-snippet:-1,max-video-preview:-1`;

  return (
    <Helmet>
      <html lang="en" />
      <title>{metaTitle}</title>
      <meta name="description" content={metaDescription} />
      {metaKeywords ? <meta name="keywords" content={metaKeywords} /> : null}
      <meta name="robots" content={robotsContent} />
      <link rel="canonical" href={canonical} />

      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={metaTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:alt" content={metaTitle} />
      <meta property="og:locale" content={locale} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={metaTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:url" content={canonical} />

      <meta name="theme-color" content="#b52619" />
      {ENV.SEARCH_CONSOLE_VERIFICATION ? (
        <meta
          name="google-site-verification"
          content={ENV.SEARCH_CONSOLE_VERIFICATION}
        />
      ) : null}
      {scripts.map((script, index) => (
        <script key={`${script.type}-${index}`} type={script.type}>
          {script.innerHTML}
        </script>
      ))}
    </Helmet>
  );
};

export default Seo;
